import { useState, useMemo, useCallback } from 'react';
import { Search, Plus, Package, Eye, EyeOff } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMaterials, useCreateMaterial, useUpdateMaterial, useDeleteMaterial, type MaterialDTO } from '../../hooks/useMaterials';
import { useAuthStore } from '../../stores/auth-store';
import { createMaterialSchema, type CreateMaterialInput, type UpdateMaterialInput } from '@shared/validation/material-schemas';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Modal } from '../../components/ui/Modal';
import { SkeletonTable } from '../../components/ui/Skeleton';

type CategoriaTab = 'todos' | 'gypsum' | 'pintura' | 'impermeabilizacion' | 'empaste' | 'general';

const CATEGORIA_LABELS: Record<CategoriaTab, string> = {
  todos: 'Todos',
  gypsum: 'Gypsum',
  pintura: 'Pintura',
  impermeabilizacion: 'Impermeabilización',
  empaste: 'Empaste',
  general: 'General',
};

const UNIDAD_OPTIONS = [
  { value: 'm2', label: 'm²' },
  { value: 'ml', label: 'ml' },
  { value: 'unid', label: 'Unidad' },
  { value: 'kg', label: 'kg' },
  { value: 'glb', label: 'Global' },
  { value: 'lt', label: 'Litro' },
  { value: 'gal', label: 'Galón' },
  { value: 'rollo', label: 'Rollo' },
  { value: 'funda', label: 'Funda' },
  { value: 'plancha', label: 'Plancha' },
];

const CATEGORIA_OPTIONS = [
  { value: 'gypsum', label: 'Gypsum' },
  { value: 'pintura', label: 'Pintura' },
  { value: 'impermeabilizacion', label: 'Impermeabilización' },
  { value: 'empaste', label: 'Empaste' },
  { value: 'general', label: 'General' },
];

export function MaterialsTab() {
  const user = useAuthStore((s) => s.user);
  const canEdit = ['administrativo', 'gerente', 'operaciones'].includes(user?.role ?? '');

  const [activeTab, setActiveTab] = useState<CategoriaTab>('todos');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [debounceTimer, setDebounceTimer] = useState<ReturnType<typeof setTimeout>>();
  const [page, setPage] = useState(1);
  const [showInactive, setShowInactive] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState<MaterialDTO | null>(null);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearch(value);
      if (debounceTimer) clearTimeout(debounceTimer);
      const timer = setTimeout(() => {
        setDebouncedSearch(value);
        setPage(1);
      }, 300);
      setDebounceTimer(timer);
    },
    [debounceTimer],
  );

  const filters = useMemo(
    () => ({
      search: debouncedSearch || undefined,
      categoria: activeTab === 'todos' ? undefined : activeTab,
      notCategoria: activeTab === 'todos' ? 'mdo' : undefined,
      isActive: showInactive ? undefined : true,
      page,
      limit: 20,
    }),
    [debouncedSearch, activeTab, page, showInactive],
  );

  const { data, isLoading, isError, refetch } = useMaterials(filters);
  const tabs: CategoriaTab[] = ['todos', 'gypsum', 'pintura', 'impermeabilizacion', 'empaste', 'general'];

  if (isLoading) {
    return <Card><div className="p-6"><SkeletonTable rows={5} cols={5} /></div></Card>;
  }

  if (isError) {
    return (
      <Card>
        <div className="px-6 py-12 text-center">
          <p className="text-sm text-gray-500">Error al cargar materiales</p>
          <Button variant="secondary" className="mt-4" onClick={() => refetch()}>Reintentar</Button>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            className="flex items-center gap-1 rounded-lg border border-gray-200 bg-white/80 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50"
            onClick={() => setShowInactive(!showInactive)}
          >
            {showInactive ? <Eye size={14} /> : <EyeOff size={14} />}
            {showInactive ? 'Ver activos' : 'Ver inactivos'}
          </button>
        </div>
        {canEdit && (
          <Button variant="primary" leftIcon={<Plus size={16} />} onClick={() => setShowCreateModal(true)}>
            Nuevo Material
          </Button>
        )}
      </div>

      {/* Search + Category Tabs */}
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          className="w-full rounded-lg border border-gray-200 bg-white/80 py-2 pl-9 pr-3 text-sm placeholder:text-gray-400 focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
          placeholder="Buscar por nombre o SKU..."
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
        />
      </div>

      <div className="flex gap-2 overflow-x-auto">
        {tabs.map((tab) => (
          <button
            key={tab}
            className={`whitespace-nowrap rounded-full px-4 py-1.5 text-xs font-medium transition-colors ${
              activeTab === tab ? 'bg-brand-blue text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            onClick={() => { setActiveTab(tab); setPage(1); }}
          >
            {CATEGORIA_LABELS[tab]}
          </button>
        ))}
      </div>

      {/* Content */}
      {data && data.data.length === 0 && !debouncedSearch ? (
        <Card>
          <div className="px-6 py-16 text-center">
            <Package size={48} className="mx-auto text-gray-300" />
            <h2 className="mt-4 text-lg font-semibold text-gray-600">No hay materiales aún</h2>
            <p className="mt-1 text-sm text-gray-400">Agrega el primer material al catálogo.</p>
            {canEdit && (
              <Button variant="primary" className="mt-4" leftIcon={<Plus size={16} />} onClick={() => setShowCreateModal(true)}>
                Crear Material
              </Button>
            )}
          </div>
        </Card>
      ) : data && data.data.length === 0 && debouncedSearch ? (
        <Card>
          <div className="px-6 py-16 text-center">
            <Search size={48} className="mx-auto text-gray-300" />
            <h2 className="mt-4 text-lg font-semibold text-gray-600">Sin resultados</h2>
            <p className="mt-1 text-sm text-gray-400">
              No encontramos materiales que coincidan con &quot;{debouncedSearch}&quot;
            </p>
          </div>
        </Card>
      ) : (
        <>
          <Card className="hidden sm:block">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    <th className="px-4 py-3">Material</th>
                    <th className="px-4 py-3">SKU</th>
                    <th className="px-4 py-3">Unidad</th>
                    <th className="px-4 py-3">Precio Unit.</th>
                    <th className="px-4 py-3">Rendimiento</th>
                    <th className="px-4 py-3">Categoría</th>
                    {showInactive && <th className="px-4 py-3">Estado</th>}
                  </tr>
                </thead>
                <tbody>
                  {data?.data.map((material) => (
                    <MaterialRow key={material.id} material={material} showInactive={showInactive} onClick={canEdit ? () => setEditingMaterial(material) : undefined} />
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <div className="space-y-3 sm:hidden">
            {data?.data.map((material) => (
              <MaterialCard key={material.id} material={material} onClick={canEdit ? () => setEditingMaterial(material) : undefined} />
            ))}
          </div>

          {data && data.pagination.totalPages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Anterior</Button>
              <span className="text-sm text-gray-500">Página {page} de {data.pagination.totalPages}</span>
              <Button variant="ghost" size="sm" disabled={page >= data.pagination.totalPages} onClick={() => setPage((p) => p + 1)}>Siguiente</Button>
            </div>
          )}
        </>
      )}

      {/* Mobile FAB */}
      {canEdit && (
        <button
          className="fixed bottom-6 right-6 flex h-14 w-14 items-center justify-center rounded-full bg-[#dd5113] text-white shadow-lg transition-transform hover:scale-105 sm:hidden"
          onClick={() => setShowCreateModal(true)}
          aria-label="Nuevo Material"
        >
          <Plus size={24} />
        </button>
      )}

      <CreateMaterialModal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} />
      {editingMaterial && (
        <EditMaterialModal material={editingMaterial} onClose={() => setEditingMaterial(null)} />
      )}
    </div>
  );
}

function MaterialRow({ material, showInactive, onClick }: { material: MaterialDTO; showInactive: boolean; onClick?: () => void }) {
  return (
    <tr
      className={`border-b border-gray-50 transition-colors hover:bg-gray-50/50 ${!material.is_active ? 'opacity-50' : ''} ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick}
    >
      <td className="px-4 py-3">
        <div>
          <p className="font-medium text-gray-800">{material.nombre}</p>
          {material.presentacion && <p className="text-xs text-gray-400">{material.presentacion}</p>}
        </div>
      </td>
      <td className="px-4 py-3 text-gray-600">{material.sku ?? '—'}</td>
      <td className="px-4 py-3 text-gray-600">{material.unidad}</td>
      <td className="px-4 py-3 font-medium text-gray-800">${material.precio_unitario.toFixed(2)}</td>
      <td className="px-4 py-3 text-gray-600">
        {material.rendimiento != null
          ? `${material.rendimiento} ${material.rendimiento_unidad ?? 'm²'}`
          : <span className="text-gray-300">—</span>}
      </td>
      <td className="px-4 py-3">
        <Badge color={getCategoriaColor(material.categoria)}>{material.categoria}</Badge>
      </td>
      {showInactive && (
        <td className="px-4 py-3">
          <Badge color={material.is_active ? 'green' : 'gray'}>{material.is_active ? 'Activo' : 'Inactivo'}</Badge>
        </td>
      )}
    </tr>
  );
}

function MaterialCard({ material, onClick }: { material: MaterialDTO; onClick?: () => void }) {
  return (
    <div className={`glass-card p-4 ${!material.is_active ? 'opacity-50' : ''} ${onClick ? 'cursor-pointer' : ''}`} onClick={onClick}>
      <div className="flex items-start justify-between">
        <div>
          <p className="font-medium text-gray-800">{material.nombre}</p>
          <p className="text-xs text-gray-400">{material.sku ?? 'Sin SKU'} — {material.unidad}</p>
        </div>
        <p className="font-semibold text-gray-800">${material.precio_unitario.toFixed(2)}</p>
      </div>
      <div className="mt-2 flex gap-1">
        <Badge color={getCategoriaColor(material.categoria)}>{material.categoria}</Badge>
      </div>
    </div>
  );
}

function CreateMaterialModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const createMutation = useCreateMaterial();
  const { register, handleSubmit, formState: { errors } } = useForm<CreateMaterialInput>({
    resolver: zodResolver(createMaterialSchema),
    defaultValues: { precioUnitario: 0 },
  });

  const onSubmit = handleSubmit(async (data) => {
    await createMutation.mutateAsync({
      ...data,
      sku: data.sku?.trim() || null,
      presentacion: data.presentacion?.trim() || null,
    });
    onClose();
  });

  return (
    <Modal isOpen={isOpen} title="Nuevo Material" onClose={onClose} size="md">
      <form onSubmit={onSubmit} className="space-y-4">
        <Input label="Nombre" {...register('nombre')} error={errors.nombre?.message} placeholder="Ej: Masilla para Gypsum" />
        <div className="grid grid-cols-2 gap-4">
          <Input label="SKU (opcional)" {...register('sku')} error={errors.sku?.message} placeholder="Ej: MAT-001" />
          <Input label="Presentación (opcional)" {...register('presentacion')} error={errors.presentacion?.message} placeholder="Ej: Funda 25kg" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Select label="Unidad" {...register('unidad')} error={errors.unidad?.message} options={UNIDAD_OPTIONS} />
          <Select label="Categoría" {...register('categoria')} error={errors.categoria?.message} options={CATEGORIA_OPTIONS} />
        </div>
        <Input label="Precio Unitario" type="number" step="0.01" {...register('precioUnitario', { valueAsNumber: true })} error={errors.precioUnitario?.message} placeholder="0.00" />
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Rendimiento (m² por unidad, opcional)"
            type="number"
            step="0.01"
            {...register('rendimiento', { setValueAs: (v) => (v === '' || v === null || v === undefined) ? null : Number(v) })}
            error={errors.rendimiento?.message}
            placeholder="Ej: 20 (1 galon cubre 20m²)"
          />
          <Select
            label="Unidad de rendimiento"
            {...register('rendimientoUnidad')}
            error={errors.rendimientoUnidad?.message}
            options={[
              { value: '', label: '— ninguna —' },
              { value: 'm2', label: 'm²' },
              { value: 'ml', label: 'ml' },
            ]}
          />
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="secondary" onClick={onClose} type="button">Cancelar</Button>
          <Button variant="primary" type="submit" isLoading={createMutation.isPending}>Crear Material</Button>
        </div>
      </form>
    </Modal>
  );
}

function EditMaterialModal({ material, onClose }: { material: MaterialDTO; onClose: () => void }) {
  const updateMutation = useUpdateMaterial();
  const deleteMutation = useDeleteMaterial();
  const [nombre, setNombre] = useState(material.nombre);
  const [sku, setSku] = useState(material.sku ?? '');
  const [presentacion, setPresentacion] = useState(material.presentacion ?? '');
  const [unidad, setUnidad] = useState(material.unidad);
  const [categoria, setCategoria] = useState(material.categoria);
  const [precioUnitario, setPrecioUnitario] = useState(String(material.precio_unitario));
  const [rendimiento, setRendimiento] = useState(material.rendimiento != null ? String(material.rendimiento) : '');
  const [rendimientoUnidad, setRendimientoUnidad] = useState(material.rendimiento_unidad ?? '');

  const handleSubmit = async () => {
    if (!nombre.trim()) return;
    const data: UpdateMaterialInput = {
      nombre: nombre.trim(),
      sku: sku.trim() || null,
      presentacion: presentacion.trim() || null,
      unidad: unidad as 'm2' | 'ml' | 'unid' | 'kg' | 'glb' | 'lt' | 'gal' | 'rollo' | 'funda' | 'plancha',
      categoria: categoria as 'gypsum' | 'pintura' | 'impermeabilizacion' | 'empaste' | 'general' | 'mdo',
      precioUnitario: Number(precioUnitario),
      rendimiento: rendimiento ? Number(rendimiento) : null,
      rendimientoUnidad: rendimientoUnidad || null,
    };
    await updateMutation.mutateAsync({ id: material.id, data });
    onClose();
  };

  return (
    <Modal isOpen title="Editar Material" onClose={onClose} size="md">
      <div className="space-y-4">
        <Input label="Nombre" value={nombre} onChange={(e) => setNombre(e.target.value)} />
        <div className="grid grid-cols-2 gap-4">
          <Input label="SKU (opcional)" value={sku} onChange={(e) => setSku(e.target.value)} />
          <Input label="Presentación (opcional)" value={presentacion} onChange={(e) => setPresentacion(e.target.value)} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Select label="Unidad" value={unidad} onChange={(e) => setUnidad(e.target.value)} options={UNIDAD_OPTIONS} />
          <Select label="Categoría" value={categoria} onChange={(e) => setCategoria(e.target.value)} options={CATEGORIA_OPTIONS} />
        </div>
        <Input label="Precio Unitario" type="number" step="0.01" value={precioUnitario} onChange={(e) => setPrecioUnitario(e.target.value)} />
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Rendimiento (m² por unidad, opcional)"
            type="number"
            step="0.01"
            value={rendimiento}
            onChange={(e) => setRendimiento(e.target.value)}
            placeholder="Ej: 20"
          />
          <Select
            label="Unidad de rendimiento"
            value={rendimientoUnidad}
            onChange={(e) => setRendimientoUnidad(e.target.value)}
            options={[
              { value: '', label: '— ninguna —' },
              { value: 'm2', label: 'm²' },
              { value: 'ml', label: 'ml' },
            ]}
          />
        </div>
        <div className="flex justify-between pt-2">
          <Button variant="ghost" className="text-red-500 hover:text-red-700" onClick={async () => {
            if (window.confirm('¿Desactivar este material? No aparecerá en futuras búsquedas.')) {
              await deleteMutation.mutateAsync(material.id);
              onClose();
            }
          }}>Desactivar</Button>
          <div className="flex gap-3">
            <Button variant="secondary" onClick={onClose} type="button">Cancelar</Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              isLoading={updateMutation.isPending}
              disabled={!nombre.trim()}
            >
              Guardar Cambios
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

function getCategoriaColor(categoria: string): 'blue' | 'green' | 'orange' | 'gray' {
  switch (categoria) {
    case 'gypsum': return 'blue';
    case 'pintura': return 'green';
    case 'impermeabilizacion': return 'orange';
    default: return 'gray';
  }
}
